import React from 'react';
import { RepairOrderCard, RepairOrderPingType, RepairOrderStatusColors } from '../types/repairOrders';
import RepairOrderPingControls from './RepairOrderPingControls';

/** Status pill color, matching the Repair Orders table (RepairOrderStatusColors). */
const statusColorClass = (status: string): string => {
  const normalized = status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : '';
  return RepairOrderStatusColors[normalized] || RepairOrderStatusColors.Draft;
};

/**
 * Re-exported from its new home in utils/pingAge. It shipped from this module
 * and several callers (and its tests) import it from here, so the path stays
 * valid — but there is only ever ONE ping-age helper.
 */
export { formatPingAge } from '../utils/pingAge';

export interface RepairOrderCardListProps {
  rows: RepairOrderCard[];
  loading: boolean;
  error: string | null;
  /** Card click — the parent opens the shared RepairOrderDrawer by recno. */
  onCardClick: (recno: number) => void;
  /**
   * Opts the consumer into the ping REQUEST control (Task 11). Open-ping chips
   * render regardless — an outstanding request is useful context in both the
   * Pro drawer and the Equipment drawer — so this only gates the action.
   */
  allowPings?: boolean;
  /**
   * Fires when the user asks maintenance for an ETA / a completion status on
   * THAT repair order. The parent owns the optimistic update and the toast.
   * Only consulted when `allowPings` is true.
   */
  onRequestPing?: (recno: number, requestType: RepairOrderPingType) => void;
}

/**
 * Small clickable repair-order cards (status, reported-by, part, latest stage
 * date). Shared by the Pro Details ROs sub-tab (RepairOrdersPanel) and the
 * equipment ROs sidebar on the Repair Orders page (EquipmentRepairOrdersDrawer)
 * — "the view we have on pros that list ROs" — so the two stay identical.
 */
const RepairOrderCardList: React.FC<RepairOrderCardListProps> = ({
  rows,
  loading,
  error,
  onCardClick,
  allowPings = false,
  onRequestPing,
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-4" role="status" aria-label="Loading">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-600" />
        <span className="text-sm text-gray-400 dark:text-gray-500">Loading…</span>
      </div>
    );
  }
  if (error) {
    return <div className="py-4 text-center text-xs text-red-600 dark:text-red-300">{error}</div>;
  }
  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center py-4 text-sm text-gray-400 dark:text-gray-500">
        No Repair Orders
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      {rows.map((row, index) => (
        <div
          key={row.recno}
          role="button"
          tabIndex={0}
          onClick={() => onCardClick(row.recno)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onCardClick(row.recno);
            }
          }}
          className="flex items-center gap-2 px-2 py-1.5 rounded border border-gray-200 bg-panel hover:bg-highlight dark:border-gray-700 cursor-pointer transition-colors"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span
                className="text-xs font-medium text-body truncate"
                title={row.category || row.repairPart}
              >
                {row.category || row.repairPart || 'Repair Order'}
              </span>
              {row.orderStatus && (
                <span className={`flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[0.625rem] font-medium ${statusColorClass(row.orderStatus)}`}>
                  {row.orderStatus}
                </span>
              )}
            </div>
            {(row.issue || row.repairPart) && (
              <div className="mt-0.5 text-[0.6875rem] text-gray-600 dark:text-gray-300 truncate" title={row.issue}>
                {[row.issue, row.repairPart].filter(Boolean).join(' · ')}
              </div>
            )}
            {row.vendorName && (
              <div
                className="mt-0.5 text-[0.6875rem] text-gray-600 dark:text-gray-300 truncate"
                data-testid="ro-vendor-line"
                title={`${row.vendorName} · ${row.vendorEta || row.repairEta || 'no ETA'}`}
              >
                {row.vendorName}
                {' · '}
                {row.vendorEta || row.repairEta ? (
                  <span>ETA {row.vendorEta || row.repairEta}</span>
                ) : (
                  <span className="text-muted">no ETA</span>
                )}
              </div>
            )}
            <RepairOrderPingControls
              pings={row.openPings}
              allowRequest={allowPings}
              // A Completed / Closed / Cancelled repair order keeps its chips
              // but loses the request buttons — there is no ETA left to ask for.
              orderStatus={row.orderStatus}
              onRequest={(requestType) => onRequestPing?.(row.recno, requestType)}
              // Repeated instances share one componentId; decorate only the
              // first card in permissions mode.
              showHandle={index === 0}
            />
            <div className="flex items-center gap-2 mt-0.5 text-[0.625rem] text-muted">
              <span>#{row.recno}</span>
              {row.reportedByName && <span>· {row.reportedByName}</span>}
              {row.latestDate && (
                <span className="text-gray-400 dark:text-gray-500">
                  · {row.latestDateLabel}: {row.latestDate}
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default RepairOrderCardList;
